/**
 * Permission management hook for role-based access control.
 * 
 * Provides utilities for checking user permissions and roles
 * to control UI element visibility and feature access.
 * 
 * **Requirements: 14.3, 14.4**
 */
import { useAuthStore } from '../stores/authStore'

export const usePermissions = () => {
  const {
    user,
    hasPermission,
    hasAnyPermission,
    hasRole,
    canAccessConfiguration
  } = useAuthStore()

  /**
   * Check if user is a superuser (has all permissions)
   */
  const isSuperuser = () => {
    return user?.is_superuser === true
  }

  /**
   * Check if user has the IT Administrator role (real role name — there is
   * no separate "system admin"/"integration admin" role in RBAC data).
   */
  const isSystemAdmin = () => {
    return isSuperuser() || hasRole('IT Administrator')
  }

  /**
   * Alias for isSystemAdmin — kept as a separate name since some UI copy
   * refers to "integration admin," but both collapse to the same real role.
   */
  const isIntegrationAdmin = () => {
    return isSuperuser() || hasRole('IT Administrator')
  }

  /**
   * Check if user can manage configurations
   * (system_admin, integration_admin, or has specific permissions)
   */
  const canManageConfigurations = () => {
    return canAccessConfiguration()
  }

  /**
   * Check if user can view sensitive credentials
   * (typically restricted to system_admin or superuser)
   */
  const canViewCredentials = () => {
    return isSuperuser() || isSystemAdmin()
  }

  /**
   * Check if user can export configurations
   */
  const canExportConfigurations = () => {
    return canManageConfigurations()
  }

  /**
   * Check if user can import configurations
   */
  const canImportConfigurations = () => {
    return canManageConfigurations()
  }

  /**
   * Check if user can delete configurations
   */
  const canDeleteConfigurations = () => {
    return isSuperuser() || isSystemAdmin()
  }

  /**
   * Check if user can access audit logs
   */
  const canAccessAuditLogs = () => {
    return canManageConfigurations()
  }

  /**
   * Check if user can rollback configurations
   */
  const canRollbackConfigurations = () => {
    return isSuperuser() || isSystemAdmin()
  }

  return {
    user,
    isSuperuser,
    isSystemAdmin,
    isIntegrationAdmin,
    canManageConfigurations,
    canViewCredentials,
    canExportConfigurations,
    canImportConfigurations,
    canDeleteConfigurations,
    canAccessAuditLogs,
    canRollbackConfigurations,
    hasPermission,
    hasAnyPermission,
    hasRole
  }
}
